import { getInitials } from '@/utils';
import { useState } from 'react';

/**
 * ConversationsList component for displaying and selecting conversations
 * @param {Object} props - Component props
 * @param {Array} props.conversations - Array of conversation objects
 * @param {Function} props.onConversationSelect - Callback when a conversation is selected
 * @param {Object} props.selectedConversation - Currently selected conversation
 * @param {Function} props.onNewChat - Callback when new chat button is clicked
 * @returns {JSX.Element} ConversationsList component
 */
const ConversationsList = ({
  conversations = [],
  onConversationSelect,
  selectedConversation,
  onNewChat,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter conversations based on search term
  const filteredConversations = (conversations || []).filter(conversation => {
    const contact = conversation.contact || conversation.contactId;
    return (
      contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact?.phoneNumber?.includes(searchTerm) ||
      contact?.phone?.includes(searchTerm) ||
      conversation.lastMessage?.content
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      conversation.lastMessageContent
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  });

  const formatLastMessageTime = timestamp => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();

    const isToday = date.toDateString() === now.toDateString();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } else if (isYesterday) {
      return 'Yesterday';
    } else {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    }
  };

  return (
    <div className="w-1/4 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-semibold text-gray-900">Conversations</h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={onRefresh}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh Conversations"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            <button
              onClick={onNewChat}
              className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center space-x-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>New Chat</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          {filteredConversations.length} conversations
        </p>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchTerm
              ? 'No conversations found'
              : 'No conversations available'}
          </div>
        ) : (
          filteredConversations.map(conversation => {
            const contact = conversation.contact || conversation.contactId;

            const msgContent =
              conversation.lastMessage?.content ||
              conversation.lastMessageContent;
            const msgType =
              conversation.lastMessage?.type ||
              conversation.lastMessageType ||
              'text';
            const direction =
              conversation.lastMessage?.direction ||
              conversation.lastMessageDirection;
            const status =
              conversation.lastMessage?.status ||
              conversation.lastMessageStatus;

            const isUnread = direction === 'inbound' && status === 'delivered';
            const isFailed = direction === 'outbound' && status === 'failed';

            return (
              <div
                key={conversation._id || conversation.id}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedConversation?._id === conversation._id
                    ? 'bg-blue-50 border-l-4 border-l-blue-500'
                    : ''
                }`}
                onClick={() => onConversationSelect(conversation)}
              >
                <div className="flex items-center space-x-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {contact?.avatar ? (
                      <img
                        src={contact.avatar}
                        alt={contact.name}
                        className="w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {getInitials(contact?.name || 'Unknown')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Conversation Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3
                        className={`text-sm truncate ${
                          isUnread
                            ? 'font-bold text-gray-900'
                            : 'font-medium text-gray-900'
                        }`}
                      >
                        {contact?.name || 'Unknown Contact'}
                      </h3>
                      <span
                        className={`text-xs ${
                          isUnread
                            ? 'text-primary-600 font-bold'
                            : 'text-gray-500'
                        }`}
                      >
                        {formatLastMessageTime(
                          conversation.lastMessage?.timestamp ||
                            conversation.lastMessageAt ||
                            conversation.updatedAt
                        )}
                      </span>
                    </div>
                    {(() => {
                      if (!msgContent && msgType === 'text') {
                        return (
                          <p className="text-sm text-gray-500 truncate">
                            {contact?.phoneNumber ||
                              contact?.phone ||
                              'No phone number'}
                          </p>
                        );
                      }

                      let displayLabel = msgContent;
                      if (msgType === 'image') displayLabel = '📷 Image';
                      else if (msgType === 'video') displayLabel = '📹 Video';
                      else if (msgType === 'audio') displayLabel = '🎵 Audio';
                      else if (msgType === 'document')
                        displayLabel = '📄 Document';
                      else if (msgType === 'voice')
                        displayLabel = '🎤 Voice Message';
                      else if (msgType === 'sticker')
                        displayLabel = '✨ Sticker';
                      else if (msgType === 'location')
                        displayLabel = '📍 Location';
                      else if (msgType === 'contacts')
                        displayLabel = '👤 Contact';
                      else if (
                        msgType === 'text' &&
                        displayLabel?.length > 65
                      ) {
                        displayLabel = displayLabel.substring(0, 65) + '...';
                      }

                      return (
                        <div
                          className={`text-sm truncate flex items-center ${
                            isUnread
                              ? 'font-bold text-gray-900'
                              : 'text-gray-500'
                          }`}
                        >
                          {direction === 'outbound' && (
                            <div className="flex mr-1.5 min-w-4 items-center">
                              {(() => {
                                if (status === 'sent') {
                                  return (
                                    <svg
                                      className="w-3 h-3 text-gray-400"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                }
                                if (status === 'delivered') {
                                  return (
                                    <div className="flex -space-x-2">
                                      <svg
                                        className="w-3 h-3 text-gray-400"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      <svg
                                        className="w-3 h-3 text-gray-400"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  );
                                }
                                if (status === 'read') {
                                  return (
                                    <div className="flex -space-x-2">
                                      <svg
                                        className="w-3 h-3 text-sky-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                      <svg
                                        className="w-3 h-3 text-sky-500"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  );
                                }
                                if (status === 'failed') {
                                  return (
                                    <svg
                                      className="w-3 h-3 text-red-500"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}
                          <span className="truncate">
                            {displayLabel || 'No message content'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Unread indicator */}
                  {(conversation.unreadCount > 0 || isUnread || isFailed) && (
                    <div className="flex-shrink-0">
                      {conversation.unreadCount > 0 ? (
                        <div className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {conversation.unreadCount}
                        </div>
                      ) : isUnread ? (
                        <div className="w-2.5 h-2.5 bg-primary-500 rounded-full"></div>
                      ) : (
                        <div className="text-red-500">
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationsList;
